import { useState, type FormEvent } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { Button, Card, PageState, useToast } from '../../components/ui';
import { useAuth } from '../../auth/AuthContext';
import {
  useAccessRequests,
  useCreateAccessRequest,
  useMenuCatalog,
} from '../../api/access-requests';

function when(iso: string): string {
  return new Date(iso).toLocaleString();
}

const STATUS_TONE: Record<string, string> = {
  PENDING: 'var(--color-text-muted)',
  APPROVED: 'var(--color-success, #1a7f37)',
  REJECTED: 'var(--color-danger)',
};

export function RequestAccessPage() {
  const { hasPermission } = useAuth();
  const canRequest = hasPermission('settings.manage');
  const catalog = useMenuCatalog();
  const history = useAccessRequests();
  const create = useCreateAccessRequest();
  const toast = useToast();

  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [note, setNote] = useState('');

  if (!canRequest) return <Navigate to="/" replace />;

  function toggle(key: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (selected.size === 0) return;
    try {
      await create.mutateAsync({ menuKeys: [...selected], note: note.trim() || undefined });
      setSelected(new Set());
      setNote('');
      toast.success('Access request submitted.');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not submit the request.');
    }
  }

  const notYetGranted = (catalog.data ?? []).filter((m) => !m.granted);
  const groups = [...new Set(notYetGranted.map((m) => m.groupLabel))];

  return (
    <div style={{ display: 'grid', gap: 'var(--space-4)', maxWidth: 820 }}>
      <h1 style={{ margin: 0, fontSize: 'var(--font-size-xl)' }}>Request Access</h1>
      <p style={{ margin: 0, color: 'var(--color-text-muted)' }}>
        Ask the platform to enable additional menus for this workspace. A platform admin reviews
        each request.
      </p>

      <Card title="Available to request">
        {catalog.isPending ? (
          <PageState kind="loading" message="Loading…" />
        ) : catalog.isError ? (
          <PageState
            kind="error"
            message={catalog.error.message}
            onRetry={() => void catalog.refetch()}
          />
        ) : notYetGranted.length === 0 ? (
          <PageState kind="empty" title="Everything is already enabled" />
        ) : (
          <form onSubmit={submit} style={{ display: 'grid', gap: 'var(--space-3)' }}>
            {groups.map((group) => (
              <fieldset key={group} style={{ border: 'none', margin: 0, padding: 0 }}>
                <legend
                  style={{
                    fontSize: 'var(--font-size-sm)',
                    fontWeight: 600,
                    color: 'var(--color-text-muted)',
                    marginBottom: 'var(--space-1)',
                  }}
                >
                  {group}
                </legend>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-3)' }}>
                  {notYetGranted
                    .filter((m) => m.groupLabel === group)
                    .map((m) => (
                      <label
                        key={m.key}
                        style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-1)' }}
                      >
                        <input
                          type="checkbox"
                          checked={selected.has(m.key)}
                          onChange={() => toggle(m.key)}
                        />
                        {m.label}
                      </label>
                    ))}
                </div>
              </fieldset>
            ))}
            <textarea
              className="ne-input"
              rows={3}
              placeholder="Why do you need this? (optional)"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              aria-label="Note"
            />
            <div>
              <Button type="submit" disabled={create.isPending || selected.size === 0}>
                {create.isPending ? 'Submitting…' : 'Submit request'}
              </Button>
            </div>
          </form>
        )}
      </Card>

      <Card title="Request history">
        {history.isPending ? (
          <PageState kind="loading" message="Loading…" />
        ) : history.isError ? (
          <PageState kind="error" message={history.error.message} />
        ) : history.data.items.length === 0 ? (
          <PageState kind="empty" title="No requests yet" />
        ) : (
          <div style={{ display: 'grid', gap: 'var(--space-3)' }}>
            {history.data.items.map((req) => (
              <div
                key={req.id}
                style={{
                  borderBottom: '1px solid var(--color-border)',
                  paddingBottom: 'var(--space-2)',
                }}
              >
                <div style={{ color: 'var(--color-text-muted)', fontSize: 'var(--font-size-sm)' }}>
                  {when(req.createdAt)}
                  {req.note ? ` · "${req.note}"` : ''}
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-2)' }}>
                  {req.items.map((item) => (
                    <span
                      key={item.id}
                      className="ne-badge"
                      style={{ color: STATUS_TONE[item.status] }}
                    >
                      {item.menuLabel}: {item.status}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      <p style={{ margin: 0, fontSize: 'var(--font-size-sm)' }}>
        <Link to="/">← Back to dashboard</Link>
      </p>
    </div>
  );
}
